'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function MulchCalculator() {
  const t = useTranslations("tools.mulchCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [areaMode, setAreaMode] = useState<'total' | 'dimensions'>('total');
  const [totalArea, setTotalArea] = useState(200);
  const [totalAreaUnit, setTotalAreaUnit] = useState('sqft');
  const [dimensions, setDimensions] = useState([{ length: 0, width: 0, unit: 'ft' }]);
  const [depth, setDepth] = useState(2);
  const [depthUnit, setDepthUnit] = useState('in');
  const [price, setPrice] = useState(0);
  const [priceUnit, setPriceUnit] = useState('cuft');
  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)' },
      {},
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        textAlign: 'center' as const
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    toolHeaderTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {},
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    formSection: getResponsiveStyle(
      {
        marginBottom: '2rem',
        background: '#f8fafc',
        borderRadius: '0.75rem',
        border: '2px solid #e2e8f0'
      },
      { padding: '1.25rem' },
      { padding: '1.5rem' },
      { padding: '1.5rem' }
    ),
    formSectionTitle: getResponsiveStyle(
      { margin: '0 0 1.5rem 0', color: '#1e293b', fontWeight: 600 },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    formGroup: {
      marginBottom: '1.5rem'
    },
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', color: '#334155', fontWeight: 500 },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const
      },
      { padding: '0.75rem' },
      { padding: '0.75rem' },
      { padding: '0.75rem' }
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
        background: 'white'
      },
      { padding: '0.75rem' },
      { padding: '0.75rem' },
      { padding: '0.75rem' }
    ),
    radioGroup: getResponsiveStyle(
      { display: 'flex', marginBottom: '1rem' },
      { flexDirection: 'column' as const, gap: '0.75rem' },
      { flexDirection: 'row' as const, gap: '1.5rem' },
      { flexDirection: 'row' as const, gap: '1.5rem' }
    ),
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      fontWeight: 500,
      color: '#475569'
    },
    radioInput: {
      width: 'auto',
      marginRight: '0.5rem',
      cursor: 'pointer'
    },
    dimensionInputs: getResponsiveStyle(
      { display: 'grid', gap: '1rem', marginBottom: '1rem', alignItems: 'end' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr 1fr' }
    ),
    dimensionHeader: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#64748b',
      marginBottom: '0.5rem',
      textAlign: 'center' as const
    },
    addMoreBtn: {
      background: 'transparent',
      color: '#2563eb',
      border: 'none',
      padding: '0.5rem',
      cursor: 'pointer',
      fontSize: '0.95rem',
      fontWeight: 500,
      textDecoration: 'underline'
    },
    inputWithUnit: {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: '0.5rem'
    },
    calculateBtn: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        fontWeight: 600,
        borderRadius: '0.75rem',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
        width: '100%',
        marginTop: '1rem'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' },
      { padding: '1rem 3rem', fontSize: '1.1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderTop: '3px solid #10b981'
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { margin: '0 0 1.5rem 0', color: '#065f46', textAlign: 'center' as const },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    resultGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
    ),
    resultCard: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
      borderLeft: '4px solid #2563eb'
    },
    resultLabel: {
      display: 'block',
      color: '#64748b',
      fontSize: '0.875rem',
      fontWeight: 500,
      marginBottom: '0.5rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px'
    },
    resultValue: {
      display: 'block',
      color: '#1e293b',
      fontSize: '1.75rem',
      fontWeight: 700
    },
    resultValueHighlight: {
      color: '#2563eb'
    },
    costResult: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderLeft: '4px solid #f59e0b',
      marginTop: '1rem',
      padding: '1.5rem',
      borderRadius: '0.75rem'
    },
    costResultValue: {
      color: '#92400e'
    },
    infoTip: getResponsiveStyle(
      {
        background: '#dbeafe',
        color: '#1e40af',
        borderRadius: '0.5rem',
        marginTop: '1rem',
        borderLeft: '4px solid #2563eb'
      },
      { padding: '0.875rem', fontSize: '0.875rem' },
      { padding: '1rem', fontSize: '0.9rem' },
      { padding: '1rem', fontSize: '0.9rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem'
    },
    strong: {
      fontWeight: 600,
      color: colors.textPrimary
    },
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.1875rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      borderBottom: `1px solid ${colors.primary}`,
      transition: 'all 0.3s ease'
    }
  };

  const convertToSquareFeet = (value: number, unit: string): number => {
    const conversions: { [key: string]: number } = {
      'sqft': 1,
      'sqm': 10.7639,
      'sqyd': 9,
      'acres': 43560
    };
    return value * conversions[unit];
  };

  const convertToFeet = (value: number, unit: string): number => {
    const conversions: { [key: string]: number } = {
      'ft': 1,
      'm': 3.28084,
      'yd': 3,
      'in': 1/12,
      'cm': 0.0328084
    };
    return value * conversions[unit];
  };

  const calculateMulch = (areaSqFt: number, depthInches: number) => {
    const cubicFeet = (areaSqFt * depthInches) / 12;
    const cubicYards = cubicFeet / 27;
    return { cubicFeet, cubicYards };
  };

  const handleAddDimension = () => {
    if (dimensions.length < 10) {
      setDimensions([...dimensions, { length: 0, width: 0, unit: 'ft' }]);
    }
  };

  const handleDimensionChange = (index: number, field: string, value: any) => {
    const newDimensions = [...dimensions];
    newDimensions[index] = { ...newDimensions[index], [field]: value };
    setDimensions(newDimensions);
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let totalAreaSqFt = 0;

    if (areaMode === 'total') {
      totalAreaSqFt = convertToSquareFeet(totalArea, totalAreaUnit);
    } else {
      dimensions.forEach(dim => {
        if (dim.length > 0 && dim.width > 0) {
          const lengthFt = convertToFeet(dim.length, dim.unit);
          const widthFt = convertToFeet(dim.width, dim.unit);
          totalAreaSqFt += lengthFt * widthFt;
        }
      });
    }

    const depthInches = convertToFeet(depth, depthUnit) * 12;

    if (totalAreaSqFt <= 0 || depth <= 0) {
      alert(t('validationError'));
      return;
    }

    const { cubicFeet, cubicYards } = calculateMulch(totalAreaSqFt, depthInches);
    const bags2cuft = Math.ceil(cubicFeet / 2);

    let totalCost = 0;
    if (price > 0) {
      switch(priceUnit) {
        case 'cuft':
          totalCost = price * cubicFeet;
          break;
        case 'cuyd':
          totalCost = price * cubicYards;
          break;
        case 'cum':
          totalCost = price * (cubicFeet / 35.3147);
          break;
        case 'bag2':
          totalCost = price * bags2cuft;
          break;
        case 'bag3':
          totalCost = price * Math.ceil(cubicFeet / 3);
          break;
      }
    }

    setResults({
      area: totalAreaSqFt,
      cubicYards,
      cubicFeet,
      bags: bags2cuft,
      cost: totalCost
    });
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolHeaderTitle}>🌱 {t('toolTitle')}</h2>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            <div style={styles.formSection}>
              <h3 style={styles.formSectionTitle}>{t('areaToCover')}</h3>

              <div style={styles.radioGroup}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="areaMode"
                    value="total"
                    checked={areaMode === 'total'}
                    onChange={() => setAreaMode('total')}
                    style={styles.radioInput}
                  />
                  {t('useTotalArea')}
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="areaMode"
                    value="dimensions"
                    checked={areaMode === 'dimensions'}
                    onChange={() => setAreaMode('dimensions')}
                    style={styles.radioInput}
                  />
                  {t('useDimensions')}
                </label>
              </div>

              {areaMode === 'total' && (
                <div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('totalAreaLabel')}</label>
                    <div style={styles.inputWithUnit}>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={totalArea}
                        onChange={(e) => setTotalArea(parseFloat(e.target.value) || 0)}
                        style={styles.input}
                      />
                      <select
                        value={totalAreaUnit}
                        onChange={(e) => setTotalAreaUnit(e.target.value)}
                        style={styles.select}
                      >
                        <option value="sqft">{t('squareFeet')}</option>
                        <option value="sqm">{t('squareMeters')}</option>
                        <option value="sqyd">{t('squareYards')}</option>
                        <option value="acres">{t('acres')}</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {areaMode === 'dimensions' && (
                <div>
                  {dimensions.map((dim, index) => (
                    <div key={index} style={{ marginBottom: '0.75rem' }}>
                      <div style={styles.dimensionInputs}>
                        <div>
                          {index === 0 && <div style={styles.dimensionHeader}>{t('length')}</div>}
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0"
                            value={dim.length || ''}
                            onChange={(e) => handleDimensionChange(index, 'length', parseFloat(e.target.value) || 0)}
                            style={styles.input}
                          />
                        </div>
                        <div>
                          {index === 0 && <div style={styles.dimensionHeader}>{t('width')}</div>}
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0"
                            value={dim.width || ''}
                            onChange={(e) => handleDimensionChange(index, 'width', parseFloat(e.target.value) || 0)}
                            style={styles.input}
                          />
                        </div>
                        <div>
                          {index === 0 && <div style={styles.dimensionHeader}>{t('unit')}</div>}
                          <select
                            value={dim.unit}
                            onChange={(e) => handleDimensionChange(index, 'unit', e.target.value)}
                            style={styles.select}
                          >
                            <option value="ft">{t('feet')}</option>
                            <option value="m">{t('meters')}</option>
                            <option value="yd">{t('yards')}</option>
                            <option value="in">{t('inches')}</option>
                            <option value="cm">{t('cm')}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                  {dimensions.length < 10 && (
                    <button type="button" style={styles.addMoreBtn} onClick={handleAddDimension}>
                      + {t('addMoreAreas')}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div style={styles.formSection}>
              <h3 style={styles.formSectionTitle}>{t('mulchLayerDepth')}</h3>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('depthLabel')}</label>
                <div style={styles.inputWithUnit}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={depth}
                    onChange={(e) => setDepth(parseFloat(e.target.value) || 0)}
                    style={styles.input}
                    required
                  />
                  <select
                    value={depthUnit}
                    onChange={(e) => setDepthUnit(e.target.value)}
                    style={styles.select}
                  >
                    <option value="in">{t('inches')}</option>
                    <option value="cm">{t('cm')}</option>
                    <option value="ft">{t('feet')}</option>
                    <option value="m">{t('meters')}</option>
                    <option value="yd">{t('yards')}</option>
                  </select>
                </div>
              </div>
              <div style={styles.infoTip}>
                💡 {t('depthRecommendation')}
              </div>
            </div>

            <div style={styles.formSection}>
              <h3 style={styles.formSectionTitle}>{t('priceOptional')}</h3>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('pricePerUnit')}</label>
                <div style={styles.inputWithUnit}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={price || ''}
                    onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                    style={styles.input}
                  />
                  <select
                    value={priceUnit}
                    onChange={(e) => setPriceUnit(e.target.value)}
                    style={styles.select}
                  >
                    <option value="cuft">{t('perCubicFoot')}</option>
                    <option value="cuyd">{t('perCubicYard')}</option>
                    <option value="cum">{t('perCubicMeter')}</option>
                    <option value="bag2">{t('perBag2')}</option>
                    <option value="bag3">{t('perBag3')}</option>
                  </select>
                </div>
              </div>
            </div>

            <button type="submit" style={styles.calculateBtn}>
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>📊 {t('yourResults')}</h3>
              <div style={styles.resultGrid}>
                <div style={styles.resultCard}>
                  <span style={styles.resultLabel}>{t('totalArea')}</span>
                  <span style={styles.resultValue}>{results.area.toFixed(2)} {t('sqFt')}</span>
                </div>
                <div style={styles.resultCard}>
                  <span style={styles.resultLabel}>{t('cubicYards')}</span>
                  <span style={{...styles.resultValue, ...styles.resultValueHighlight}}>
                    {results.cubicYards.toFixed(2)} {t('yd3')}
                  </span>
                </div>
                <div style={styles.resultCard}>
                  <span style={styles.resultLabel}>{t('cubicFeet')}</span>
                  <span style={styles.resultValue}>{results.cubicFeet.toFixed(2)} {t('ft3')}</span>
                </div>
                <div style={styles.resultCard}>
                  <span style={styles.resultLabel}>{t('numberOfBags')}</span>
                  <span style={styles.resultValue}>{results.bags} {t('bags')}</span>
                </div>
              </div>
              {results.cost > 0 && (
                <div style={styles.costResult}>
                  <span style={styles.resultLabel}>💰 {t('estimatedCost')}</span>
                  <span style={{...styles.resultValue, ...styles.costResultValue}}>
                    ${results.cost.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}><strong style={styles.strong}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('step4Title')}</strong> {t('step4Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('step5Title')}</strong> {t('step5Description')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>

        <h3 style={styles.h3}>{t('insightDepthTitle')}</h3>
        <p style={styles.paragraph}>{t('insightDepthParagraph1')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong style={styles.strong}>{t('depth2Inches')}</strong> {t('depth2Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('depth3Inches')}</strong> {t('depth3Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('depth4Inches')}</strong> {t('depth4Description')}</li>
        </ul>
        <p style={styles.paragraph}>{t('insightDepthParagraph2')}</p>

        <h3 style={styles.h3}>{t('insightBenefitsTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong style={styles.strong}>{t('benefitMoisture')}</strong> {t('benefitMoistureDescription')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('benefitTemperature')}</strong> {t('benefitTemperatureDescription')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('benefitWeeds')}</strong> {t('benefitWeedsDescription')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('benefitErosion')}</strong> {t('benefitErosionDescription')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('benefitNutrients')}</strong> {t('benefitNutrientsDescription')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('benefitAesthetics')}</strong> {t('benefitAestheticsDescription')}</li>
        </ul>

        <h3 style={styles.h3}>{t('insightBestPracticesTitle')}</h3>
        <p style={styles.paragraph}>{t('insightBestPracticesParagraph1')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong style={styles.strong}>{t('practiceArea')}</strong> {t('practiceAreaDescription')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('practiceDepth')}</strong> {t('practiceDepthDescription')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('practiceOrdering')}</strong> {t('practiceOrderingDescription')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('practiceApplication')}</strong> {t('practiceApplicationDescription')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>

        <h3 style={styles.h3}>{t('detailsFormulaTitle')}</h3>
        <p style={styles.paragraph}>{t('detailsFormulaParagraph1')}</p>
        <p style={styles.paragraph}><strong style={styles.strong}>{t('formulaBulk')}</strong></p>
        <p style={styles.paragraph}><strong style={styles.strong}>{t('formulaBags')}</strong></p>

        <h3 style={styles.h3}>{t('detailsConversionsTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('conversion1')}</li>
          <li style={styles.listItem}>{t('conversion2')}</li>
          <li style={styles.listItem}>{t('conversion3')}</li>
          <li style={styles.listItem}>{t('conversion4')}</li>
        </ul>

        <h3 style={styles.h3}>{t('detailsTypesTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong style={styles.strong}>{t('typeOrganic')}</strong> {t('typeOrganicDescription')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('typeInorganic')}</strong> {t('typeInorganicDescription')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            <a
              href="https://www.kirkallenlandscapesupply.com/how-to-calculate-mulch-for-your-yard/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.lowes.com/n/calculators/mulch-and-soil-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.lumberjacks.com/how-much-mulch-do-i-need"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference3')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}